<template>
  <div class="position-search-modal">
    <div class="search-container">
      <h3 class="modal-title">{{ modalTitle }}</h3>
      <p class="modal-subtitle">{{ modalSubtitle }}</p>

      <div class="search-box">
        <div class="search-options">
          <checkbox v-model="is_search_by_company">חיפוש לפי שם חברה</checkbox>
        </div>

        <div class="search-input-group">
          <input
            dir="rtl"
            v-model="search_term"
            @keyup.enter="searchPosition"
            class="search-input"
            :placeholder="is_search_by_company ? 'הכנס שם חברה' : 'הכנס קוד משרה או שם משרה'"
          />
          <button
            @click="searchPosition"
            class="button is-info search-button"
            :class="{ 'is-loading': is_loading }"
          >
            חיפוש
          </button>
        </div>
      </div>

      <div v-if="search_results.length > 0" class="results-container">
        <div class="results-header">
          <h4>תוצאות חיפוש ({{ search_results.length }})</h4>
          <button @click="clearResults" class="button is-small is-danger">
            סגור תוצאות
          </button>
        </div>
        <div class="results-list">
          <search-modal-positions
            @input="selectPosition($event)"
            :results="search_results"
            :show_follow_button="false"
          ></search-modal-positions>
        </div>
        <div v-if="selected_position" class="selected-position-banner">
          <div class="selected-info">
            <i class="fas fa-check-circle"></i>
            <span>נבחרה משרה: {{ selected_position.position_title || selected_position.title }}</span>
          </div>
          <button
            @click="proceedToSendCv"
            class="button is-success proceed-button"
          >
            <span class="icon">
              <i :class="proceedButtonIcon"></i>
            </span>
            <span>{{ proceedButtonText }}</span>
          </button>
        </div>
      </div>

      <div v-if="!is_loading && searched && search_results.length === 0" class="no-results">
        <p>לא נמצאו תוצאות</p>
      </div>
    </div>

    <div class="modal-actions">
      <button @click="cancel" class="button is-light">
        <span class="icon">
          <i class="fas fa-times"></i>
        </span>
        <span>ביטול</span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import Checkbox from '@/components/Checkbox'
import SearchModalPositions from '@/components/SearchModalPositions'

export default {
  name: 'PositionSearchForCvModal',
  components: {
    Checkbox,
    SearchModalPositions
  },
  props: {
    payload: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      search_term: '',
      is_search_by_company: false,
      is_loading: false,
      searched: false,
      search_results: [],
      selected_position: null
    }
  },
  computed: {
    currentOffer () {
      return this.payload.offer || this.payload.data || this.payload
    },
    actualPayload () {
      // The modal system wraps our payload, so we need to access payload.payload
      return this.payload.payload || this.payload
    },
    currentPosition () {
      // For slot replacement mode, get the current position
      return this.actualPayload && this.actualPayload.currentPosition ? this.actualPayload.currentPosition : this.currentOffer
    },
    modalTitle () {
      return 'חיפוש משרה לשליחת קורות חיים'
    },
    modalSubtitle () {
      return 'חפש משרה לפי קוד או שם המשרה'
    },
    proceedButtonText () {
      // If opened from slot replacement (outsource), show "Replace Position"
      if (this.actualPayload && this.actualPayload.mode === 'slot_replacement') {
        return 'החלף משרה'
      }
      // If opened from Solel Boneh, show shorter text
      if (this.actualPayload && this.actualPayload.fromSolelBoneh) {
        return 'המשך'
      }
      return 'המשך לשליחת קורות חיים'
    },
    proceedButtonIcon () {
      return 'fas fa-paper-plane'
    }
  },
  methods: {
    async searchPosition () {
      if (!this.search_term.trim()) {
        this.$utils.emit('show_toast', { text: 'נא להזין טקסט לחיפוש' })
        return
      }

      this.is_loading = true
      this.searched = true
      this.search_results = []

      try {
        if (this.is_search_by_company) {
          await this.searchByCompany()
        } else {
          await this.searchByPosition()
        }
      } catch (error) {
        console.error('Search error:', error)
        this.$utils.emit('show_toast', { text: 'שגיאה בחיפוש' })
      } finally {
        this.is_loading = false
      }
    },

    async searchByCompany () {
      try {
        const companies = await this.$network_utils.caller.request(
          'get',
          '/companies/',
          'admin_headers',
          { name: this.search_term }
        )

        if (companies && companies.length > 0) {
          const positions = await this.$network_utils.getCompanyPositions({
            current_company_id: companies[0].id
          })
          this.search_results = this.filterResults(positions)
        } else {
          this.search_results = []
        }
      } catch (error) {
        console.error('Company search error:', error)
        this.search_results = []
      }
    },

    async searchByPosition () {
      try {
        const id = this.$utils.isNumeric(this.search_term) ? this.search_term : ''
        const results = await this.$network_utils.caller.request(
          'get',
          '/positions/' + id,
          'admin_headers',
          {
            position_title: this.search_term,
            statuses: 'active,editing',
            sort: 'status'
          }
        )

        const resultsArray = Array.isArray(results) ? results : [results]
        this.search_results = this.filterResults(resultsArray)
      } catch (error) {
        console.error('Position search error:', error)
        this.search_results = []
      }
    },

    filterResults (results) {
      // Filter out the current position if it exists
      if (!results || !Array.isArray(results)) {
        return []
      }

      const currentPositionId = this.currentOffer && this.currentOffer.position && this.currentOffer.position.id
      if (!currentPositionId) {
        return results
      }

      return results.filter(pos => pos.id !== currentPositionId)
    },

    async selectPosition (position) {
      this.selected_position = position
    },

    async proceedToSendCv () {
      if (!this.selected_position) {
        this.$utils.emit('show_toast', { text: 'נא לבחור משרה' })
        return
      }

      // Call the callback function passed from parent BEFORE closing
      // Use actualPayload which handles the modal system's wrapping
      if (this.actualPayload && this.actualPayload.onPositionSelected && typeof this.actualPayload.onPositionSelected === 'function') {
        console.log('Calling onPositionSelected callback with position:', this.selected_position)
        this.actualPayload.onPositionSelected(this.selected_position)
      } else {
        console.warn('No onPositionSelected callback found or it is not a function')
        console.warn('actualPayload structure:', this.actualPayload)
      }

      // Close this modal using the correct payload reference
      console.log('Closing modal with payload:', this.payload)
      this.remove(this.payload)
    },

    clearResults () {
      this.search_results = []
      this.searched = false
      this.search_term = ''
      this.selected_position = null
    },

    cancel () {
      this.remove(this.payload)
    },

    ...mapMutations('modals', ['remove'])
  }
}
</script>

<style scoped>
.position-search-modal {
  padding: 24px;
  direction: rtl;
  text-align: right;
  width: 800px;
  max-width: 95vw;
  background-color: #ffffff;
  border-radius: 12px;
}

.search-container {
  margin-bottom: 24px;
}

.modal-title {
  font-size: 24px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 8px;
  text-align: center;
}

.modal-subtitle {
  font-size: 14px;
  color: #7f8c8d;
  margin-bottom: 24px;
  text-align: center;
}

.search-box {
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.search-options {
  margin-bottom: 16px;
}

.search-input-group {
  display: flex;
  gap: 12px;
  align-items: center;
}

.search-input {
  flex: 1;
  padding: 12px 16px;
  font-size: 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: border-color 0.3s;
  color: #2c3e50;
  background-color: #ffffff;
}

.search-input:focus {
  outline: none;
  border-color: #3498db;
}

.search-button {
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 8px;
  min-width: 100px;
}

.results-container {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  max-height: 500px;
  overflow-y: auto;
  background-color: #ffffff;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  position: sticky;
  top: 0;
  z-index: 1;
}

.results-header h4 {
  margin: 0;
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50;
}

.results-list {
  padding: 16px;
}

.no-results {
  text-align: center;
  padding: 40px;
  color: #7f8c8d;
  font-style: italic;
}

.modal-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
}

.button {
  min-width: 120px;
  height: 40px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.button.is-info {
  background-color: #3498db;
  border-color: #3498db;
  color: white;
}

.button.is-info:hover:not(:disabled) {
  background-color: #2980b9;
  border-color: #2980b9;
}

.button.is-light {
  background-color: #ecf0f1;
  border-color: #bdc3c7;
  color: #2c3e50;
}

.button.is-light:hover {
  background-color: #d5dbdb;
  border-color: #95a5a6;
}

.button.is-danger {
  background-color: #e74c3c;
  border-color: #e74c3c;
  color: white;
}

.button.is-danger:hover {
  background-color: #c0392b;
  border-color: #c0392b;
}

.icon {
  margin-left: 8px;
}

/* Scrollbar styling */
.results-container::-webkit-scrollbar {
  width: 8px;
}

.results-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.results-container::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 4px;
}

.results-container::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

.selected-position-banner {
  padding: 16px;
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  border-top: 2px solid #28a745;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  position: sticky;
  bottom: 0;
  z-index: 2;
}

.selected-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #155724;
  font-weight: 500;
  font-size: 15px;
}

.selected-info i {
  color: #28a745;
  font-size: 20px;
}

.proceed-button {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  border: none;
  color: white;
  font-weight: 600;
  padding: 12px 24px;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
  transition: all 0.3s ease;
}

.proceed-button:hover {
  background: linear-gradient(135deg, #218838 0%, #1aa179 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.4);
}

.proceed-button:active:not(:disabled) {
  transform: translateY(0);
}

.proceed-button:disabled {
  background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%);
  cursor: not-allowed;
  opacity: 0.6;
}

.current-position-info {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  border: 2px solid #2196f3;
}

.current-position-info h4 {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: bold;
  color: #1565c0;
}

.info-row {
  margin-bottom: 8px;
  font-size: 14px;
  color: #424242;
}

.info-row:last-child {
  margin-bottom: 0;
}

.publish-outsources {
  background-color: #fff;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 13px;
  border: 1px solid #2196f3;
}

.validation-error {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #721c24;
  background-color: #f8d7da;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  border: 1px solid #f5c6cb;
  margin-top: 8px;
}

.validation-error i {
  color: #dc3545;
}

.selected-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
</style>
